import './Project.scss';
import eyeIcon from 'assets/images/icons/eye.svg';
import plusIcon from 'assets/images/icons/plus.svg';
import downloadIcon from 'assets/images/icons/download.svg';
import { Button } from 'components/Button/Button';
import useProjectVM from './useProjectVM';
import { downloadFileAsBlob } from 'utils/blob';
import editIcon from 'assets/images/icons/edit.svg';
import { Modal } from 'components/Modal';
import { Loader } from 'components/Loader/Loader';

export type ProjectPageProps = {};

export const Project: React.FC<ProjectPageProps> = () => {
    const {
        events,
        formatDate,
        selectedEvent,
        datalists,
        setSelectedEvent,
        getMimeTypeLabel,
        selectedDocument,
        setSelectedDocument,
        isDocumentModalOpen,
        setIsDocumentModalOpen,
        documentModifications,
        setDocumentModifications,
        handleUpdateDocument,
        isApiRequestLoading,
        isUserHasProject
    } = useProjectVM();

    if (isApiRequestLoading) {
        return (
            <main id="project">
                <Loader />
            </main>
        );
    }

    return (
        <>
            <main id="project">
                <div className="appointments">
                    <div className="last-appointment">
                        <h2>Mon dernier rendez-vous</h2>
                        {events.at(-1) ? (
                            <div
                                className={`appointment ${selectedEvent?.id === events.at(-1)?.id ? 'selected' : ''}`}
                                onClick={() => setSelectedEvent(events.at(-1) || null)}
                            >
                                <h4>{events.at(-1)?.label}</h4>
                                <p>Le {formatDate(events.at(-1)?.started_at || '')}</p>
                            </div>
                        ) : (
                            <p className="no-appointment">Pas de dernier rendez-vous</p>
                        )}
                    </div>
                    <div className="previous-appointments">
                        <h2>Rendez-vous précédents</h2>
                        {events.length > 1 ? (
                            <div className="previous-appointments-content">
                                {events.map((event, index) => {
                                    if (index !== events.length - 1) {
                                        return (
                                            <div
                                                className={`appointment ${selectedEvent?.id === event.id ? 'selected' : ''}`}
                                                key={index}
                                                onClick={() => setSelectedEvent(event)}
                                            >
                                                <h4>{event.label}</h4>
                                                <p>Le {formatDate(event.started_at || '')}</p>
                                            </div>
                                        );
                                    }
                                })}
                            </div>
                        ) : (
                            <p className="no-appointment">Aucun rendez-vous précédent</p>
                        )}
                    </div>
                </div>
                <div className="selected-appointment">
                    {selectedEvent ? (
                        <>
                            <h2>
                                Le {formatDate(selectedEvent.started_at || '')} : {selectedEvent.label}
                            </h2>
                            <div className="appointment-info documents">
                                <div className="title-actions">
                                    <h3>Documents liés</h3>
                                    {selectedDocument && (
                                        <div className="actions">
                                            {selectedDocument.is_my_file && (
                                                <Button
                                                    icon={<img src={editIcon} alt="" />}
                                                    iconBefore
                                                    label="Modifier le fichier"
                                                    variant="purple"
                                                    callback={() => {
                                                        setDocumentModifications(selectedDocument);
                                                        setIsDocumentModalOpen(true);
                                                    }}
                                                />
                                            )}
                                            <Button
                                                icon={<img src={eyeIcon} alt="" />}
                                                iconBefore
                                                label="Visualiser le fichier"
                                                variant="purple"
                                                callback={() => {
                                                    if (selectedDocument.view_link) {
                                                        window.open(selectedDocument.view_link, '_blank');
                                                    }
                                                }}
                                            />
                                            <Button
                                                icon={<img src={downloadIcon} alt="" />}
                                                iconBefore
                                                label="Télécharger le fichier"
                                                variant="purple"
                                                callback={() => {
                                                    downloadFileAsBlob(selectedDocument).catch((error: any) => {
                                                        console.error('Erreur lors du téléchargement:', error);
                                                    });
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="documents-list">
                                    <div className="document add" onClick={() => setSelectedDocument(null)}>
                                        <figure className="icon">
                                            <img src={plusIcon} alt="" />
                                        </figure>
                                        <p>Ajouter un document</p>
                                    </div>
                                    {selectedEvent.documents.map((document, index) => (
                                        <div
                                            className={`document ${selectedDocument?.id === document.id ? 'selected' : ''}`}
                                            key={index}
                                            onClick={() => setSelectedDocument(document)}
                                        >
                                            <figure>
                                                <img src={document.thumb_link} alt="" />
                                            </figure>
                                            <p className="type">{getMimeTypeLabel(document.mime_type)}</p>
                                            <p className="desc">{document.label}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="appointment-info details">
                                <h3>Entretien</h3>
                                {selectedEvent.communication_type && (
                                    <div className="info contact">
                                        <h4>Type de rendez-vous</h4>
                                        <p>{datalists?.communication_types?.[selectedEvent.communication_type] ?? ''}</p>
                                    </div>
                                )}
                                <div className="info persons-list">
                                    <h4>Personnes présentes</h4>
                                    <div className="persons">
                                        {selectedEvent.assistants_list.map((assistant) => (
                                            <p key={assistant.value}>{assistant.label}</p>
                                        ))}
                                        {selectedEvent.stakeholders_list.map((stakeholder) => (
                                            <p key={stakeholder.value}>{stakeholder.label}</p>
                                        ))}
                                        {selectedEvent.partners_list.map((partner) => (
                                            <p key={partner.value}>{partner.label}</p>
                                        ))}
                                        {!selectedEvent.assistants_list.length &&
                                            !selectedEvent.stakeholders_list.length &&
                                            !selectedEvent.partners_list.length && <p>Aucune personne présente</p>}
                                    </div>
                                </div>
                            </div>
                            {selectedEvent.observation && (
                                <div className="appointment-info observation">
                                    <h3>Commentaire</h3>
                                    <div dangerouslySetInnerHTML={{ __html: selectedEvent.observation }} />
                                </div>
                            )}
                        </>
                    ) : (
                        <h2>Aucun rendez-vous sélectionné</h2>
                    )}
                </div>
            </main>
            <Modal
                open={isDocumentModalOpen}
                setClose={() => {
                    setIsDocumentModalOpen(false);
                    setDocumentModifications(null);
                }}
                title="Modifier les informations du document"
                yesText="Modifier"
                noText="Annuler"
                onValidate={handleUpdateDocument}
                onClose={() => {
                    setIsDocumentModalOpen(false);
                    setDocumentModifications(null);
                }}
            >
                <div className="form">
                    <div className="form-line">
                        <div className="form-group">
                            <label>Nom du fichier</label>
                            <input
                                type="text"
                                name="label"
                                onChange={(e) => {
                                    const newLabel = e.target.value;
                                    setDocumentModifications((prev) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  label: newLabel
                                              }
                                            : null
                                    );
                                }}
                                value={documentModifications?.label || ''}
                            />
                        </div>
                    </div>
                    <div className="form-line">
                        <div className="form-group">
                            <label>Description</label>
                            <textarea
                                name="observation"
                                onChange={(e) => {
                                    const newObservation = e.target.value;
                                    setDocumentModifications((prev) =>
                                        prev
                                            ? {
                                                  ...prev,
                                                  observation: newObservation
                                              }
                                            : null
                                    );
                                }}
                                value={documentModifications?.observation || ''}
                                rows={6}
                            />
                        </div>
                    </div>
                </div>
            </Modal>
            <Modal open={!isUserHasProject} title="Accès limité" setClose={() => {}} className="no-close">
                <p style={{ textAlign: 'center' }}>Vous n'avez pas encore de projet de vie. Veuillez contacter votre APPV référent.</p>
            </Modal>
        </>
    );
};
